<template>
  <div class="gr-root">
    <section class="gr-section gr-auth">
      <div class="gr-container gr-auth__grid">
        <!-- Canonical hides this column below `md`; the split collapses at
                 the same breakpoint here and the form takes the full width. -->
        <aside class="gr-auth__aside gr-section--panel">
          <div class="gr-media gr-auth__media">
            <img
              v-if="!isPhotoBroken"
              src="/assets/images/dokumentasi-gotong-royong.webp"
              alt="Dokumentasi Gotong Royong Indonesia"
              class="gr-media__img"
              @error="isPhotoBroken = true"
            />
            <!-- A failed photo falls back to the green-tint bed, never to a
                     broken-image glyph. -->
            <div v-else class="gr-media__fallback" aria-hidden="true">
              <v-icon icon="mdi-image-off-outline" size="32" />
            </div>
          </div>
          <p class="gr-auth__caption">
            Dokumentasi kegiatan Gotong Royong Indonesia
          </p>
        </aside>

        <div class="gr-auth__panel">
          <header class="gr-head">
            <p class="gr-eyebrow">
              <span class="gr-eyebrow__index">Akun</span>
              <span class="gr-eyebrow__rule" />
            </p>
            <h1 class="gr-h2">{{ isSignUp ? "Daftar" : "Masuk" }}</h1>
            <p v-if="isSignUp" class="gr-lede">
              Selamat datang di Gotong Royong Indonesia, platform untuk
              membangun kebersamaan dan kepedulian sosial. Mulai perjalanan Anda
              dengan kami dengan berbagi beberapa informasi penting.
            </p>
            <p v-else class="gr-lede">
              Selamat datang kembali! Silakan masuk ke akun Anda.
            </p>
          </header>

          <!-- Canonical `v-tabs` become two links, so the choice lives in
                   the URL and `?type=signup` stays true in both directions.
                   `replace` keeps the history stack canonical: the back button
                   still leaves the page instead of walking the tabs. -->
          <nav class="gr-auth__switch" aria-label="Pilih masuk atau daftar">
            <NuxtLink
              replace
              :to="{ query: signInQuery }"
              class="gr-auth__switch-link"
              :class="{
                'gr-auth__switch-link--active': !isSignUp,
              }"
              :aria-current="isSignUp ? undefined : 'page'"
            >
              Masuk
            </NuxtLink>
            <NuxtLink
              replace
              :to="{ query: signUpQuery }"
              class="gr-auth__switch-link"
              :class="{ 'gr-auth__switch-link--active': isSignUp }"
              :aria-current="isSignUp ? 'page' : undefined"
            >
              Daftar
            </NuxtLink>
          </nav>

          <form class="gr-auth__form" novalidate @submit.prevent="onSubmit">
            <v-text-field
              v-if="isSignUp"
              v-model="name"
              v-bind="nameAttrs"
              :error-messages="fieldError('name')"
              :disabled="isSubmitting"
              class="gr-auth__field"
              color="primary"
              density="comfortable"
              variant="outlined"
              placeholder="Nama"
              autocomplete="name"
              label="Nama"
            />

            <v-text-field
              v-model="email"
              v-bind="emailAttrs"
              :error-messages="fieldError('email')"
              :disabled="isSubmitting"
              class="gr-auth__field"
              color="primary"
              type="email"
              density="comfortable"
              variant="outlined"
              placeholder="Email Address"
              autocomplete="email"
              label="Email"
            />

            <v-text-field
              v-if="isSignUp"
              v-model="phone"
              v-bind="phoneAttrs"
              :error-messages="fieldError('phone')"
              :disabled="isSubmitting"
              class="gr-auth__field"
              color="primary"
              density="comfortable"
              variant="outlined"
              placeholder="Nomor telepon"
              autocomplete="tel"
              label="Nomor telepon"
            />

            <v-text-field
              v-model="password"
              v-bind="passwordAttrs"
              :error-messages="fieldError('password')"
              :disabled="isSubmitting"
              :type="showPassword ? 'text' : 'password'"
              :autocomplete="isSignUp ? 'new-password' : 'current-password'"
              class="gr-auth__field"
              color="primary"
              density="comfortable"
              variant="outlined"
              placeholder="Kata sandi"
              label="Kata sandi"
              @update:model-value="checkPasswordStrength"
            >
              <!-- Canonical binds `@click:append-inner` to a bare icon,
                       which no keyboard can reach. A real button can. -->
              <template #append-inner>
                <button
                  type="button"
                  class="gr-auth__reveal"
                  :disabled="isSubmitting"
                  :aria-pressed="showPassword"
                  :aria-label="
                    showPassword
                      ? 'Sembunyikan kata sandi'
                      : 'Tampilkan kata sandi'
                  "
                  @click="showPassword = !showPassword"
                >
                  <v-icon
                    :icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                    size="20"
                    aria-hidden="true"
                  />
                </button>
              </template>
            </v-text-field>

            <div v-if="isSignUp" class="gr-auth__requirements">
              <p class="gr-auth__requirements-title">
                Kata sandi harus mengandung:
              </p>
              <ul class="gr-auth__requirements-grid">
                <li
                  v-for="rule in passwordRequirements"
                  :key="rule.key"
                  class="gr-auth__requirement"
                  :class="{
                    'gr-auth__requirement--met': rule.met,
                  }"
                >
                  <v-icon
                    :icon="rule.met ? 'mdi-check-circle' : 'mdi-circle-outline'"
                    size="16"
                    aria-hidden="true"
                  />
                  <span>
                    <!-- State is carried by the icon shape and by this
                             text, never by colour alone. -->
                    <span class="gr-auth__sr">
                      {{ rule.met ? "Terpenuhi:" : "Belum terpenuhi:" }}
                    </span>
                    {{ rule.label }}
                  </span>
                </li>
              </ul>
            </div>

            <div v-if="errorMessage" class="gr-auth__alert" role="alert">
              <v-icon
                icon="mdi-alert-circle-outline"
                size="20"
                aria-hidden="true"
              />
              <p class="gr-auth__alert-text">{{ errorMessage }}</p>
              <button
                type="button"
                class="gr-auth__alert-close"
                aria-label="Tutup pesan kesalahan"
                @click="errorMessage = ''"
              >
                <v-icon icon="mdi-close" size="18" aria-hidden="true" />
              </button>
            </div>

            <button
              type="submit"
              class="gr-btn gr-btn--solid gr-auth__submit"
              :disabled="isSubmitting"
              :aria-busy="isSubmitting"
            >
              <v-progress-circular
                v-if="isSubmitting"
                indeterminate
                size="18"
                width="2"
                aria-hidden="true"
              />
              {{ isSignUp ? "Daftar" : "Masuk" }}
            </button>
          </form>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import { useForm } from "vee-validate";
import type { LocationQuery } from "vue-router";

const HOME_ROUTE = "/";

// Identical to the canonical class: ! @ # $ % ^ & * ( ) _ + - = [ ] { } ; ' :
// " \ | , . < > / ? — the redundant escapes are dropped, the set is not.
const SPECIAL_CHARACTER = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;

const route = useRoute();
const router = useRouter();

// The tab is derived from the URL rather than held in a ref, so a deep link,
// a tab click and a browser Back all agree on one source of truth.
const isSignUp = computed(() => route.query.type === "signup");

// `?redirect=` and anything else in the URL survives a tab switch.
const signInQuery = computed<LocationQuery>(() => {
  const query = { ...route.query };
  delete query.type;
  return query;
});
const signUpQuery = computed<LocationQuery>(() => ({
  ...signInQuery.value,
  type: "signup",
}));

const errorMessage = ref("");
const showPassword = ref(false);
const isPhotoBroken = ref(false);
const formErrors = ref<Record<string, string[] | string> | null>(null);

const passwordChecks = reactive({
  minLength: false,
  hasUppercase: false,
  hasLowercase: false,
  hasNumber: false,
  hasSpecial: false,
});

const passwordRequirements = computed(() => [
  {
    key: "minLength",
    label: "Minimal 8 karakter",
    met: passwordChecks.minLength,
  },
  {
    key: "hasUppercase",
    label: "1 huruf besar",
    met: passwordChecks.hasUppercase,
  },
  {
    key: "hasLowercase",
    label: "1 huruf kecil",
    met: passwordChecks.hasLowercase,
  },
  { key: "hasNumber", label: "1 angka", met: passwordChecks.hasNumber },
  {
    key: "hasSpecial",
    label: "1 karakter spesial",
    met: passwordChecks.hasSpecial,
  },
]);

// An empty string fails every test, so this doubles as the reset the canonical
// page writes out as a separate `Object.keys(...).forEach` loop.
function checkPasswordStrength(value: string): void {
  passwordChecks.minLength = value.length >= 8;
  passwordChecks.hasUppercase = /[A-Z]/.test(value);
  passwordChecks.hasLowercase = /[a-z]/.test(value);
  passwordChecks.hasNumber = /[0-9]/.test(value);
  passwordChecks.hasSpecial = SPECIAL_CHARACTER.test(value);
}

// Copied verbatim from the canonical page, message for message.
function validateSignUpPassword(value: string): true | string {
  if (!value) return "Kata sandi diperlukan";
  if (value.length < 8) return "Kata sandi minimal 8 karakter";
  if (!/[A-Z]/.test(value))
    return "Kata sandi harus memiliki minimal 1 huruf besar";
  if (!/[a-z]/.test(value))
    return "Kata sandi harus memiliki minimal 1 huruf kecil";
  if (!/[0-9]/.test(value)) return "Kata sandi harus memiliki minimal 1 angka";
  if (!SPECIAL_CHARACTER.test(value))
    return "Kata sandi harus memiliki minimal 1 karakter spesial";
  return true;
}

interface AuthFormValues {
  name?: string;
  email?: string;
  phone?: string;
  password?: string;
}

const validationSchema = computed(() =>
  isSignUp.value
    ? {
        name: "required",
        email: "required|email",
        phone: "required|phone",
        password: validateSignUpPassword,
      }
    : { email: "required|email", password: "required|min:8" },
);

const { isSubmitting, resetForm, handleSubmit, defineField, errors } =
  useForm<AuthFormValues>({ validationSchema });

const [email, emailAttrs] = defineField("email");
const [password, passwordAttrs] = defineField("password");
const [name, nameAttrs] = defineField("name");
const [phone, phoneAttrs] = defineField("phone");

// Same precedence as the canonical page: the client rule wins, the server's
// field error is the fallback.
function fieldError(
  field: keyof AuthFormValues,
): string | string[] | undefined {
  return errors.value[field] || formErrors.value?.[field];
}

// `error.response._data.errors`, reached without an `any` cast.
function readApiFieldErrors(
  error: unknown,
): Record<string, string[] | string> | null {
  if (typeof error !== "object" || error === null) return null;
  const response = Reflect.get(error, "response");
  if (typeof response !== "object" || response === null) return null;
  const data = Reflect.get(response, "_data");
  if (typeof data !== "object" || data === null) return null;
  const fields = Reflect.get(data, "errors");
  if (typeof fields !== "object" || fields === null) return null;
  return fields as Record<string, string[] | string>;
}

// The canonical page drops `?redirect=` and always lands on the homepage, which
// strands anyone the activity detail page sent here. Honouring it reopens an
// open-redirect hole unless the value is a same-origin path, so `//host` and
// `/\host` are rejected before it is used.
function destinationAfterAuth(): string {
  const redirect = route.query.redirect;
  if (typeof redirect === "string" && /^\/(?![/\\])/.test(redirect)) {
    return redirect;
  }
  return HOME_ROUTE;
}

async function submitCredentials(values: AuthFormValues): Promise<void> {
  const { $auth } = useNuxtApp();
  if (!isSignUp.value) {
    await $auth.signIn(values.email ?? "", values.password ?? "");
    return;
  }
  await $auth.signUp({
    name: values.name ?? "",
    email: values.email ?? "",
    phone: values.phone ?? "",
    password: values.password ?? "",
  });
}

const onSubmit = handleSubmit(async (values) => {
  errorMessage.value = "";
  formErrors.value = null;

  // Checked before any network call, exactly where the canonical page checks it.
  if (isSignUp.value && !Object.values(passwordChecks).every(Boolean)) {
    errorMessage.value = "Kata sandi tidak memenuhi persyaratan keamanan";
    return;
  }

  try {
    await submitCredentials(values);
    router.push(destinationAfterAuth());
  } catch (error: unknown) {
    formErrors.value = readApiFieldErrors(error);
    errorMessage.value = getErrorMessage(error);
  }
});

// Canonical resets the form and the checklist on every tab change. It leaves
// `formErrors` behind, so a server-side "email sudah terdaftar" survives into
// the sign-in tab; that one is cleared here too.
watch(isSignUp, () => {
  resetForm();
  errorMessage.value = "";
  formErrors.value = null;
  checkPasswordStrength("");
});
</script>

<style scoped src="@/features/auth/views/auth.css"></style>
